import { useForm, yupResolver } from "@mantine/form";
import {
  CreateInitialChargeInput,
  GetAgreementQuery,
  useCreateInitialChargeMutation,
  useDeleteInitialChargeMutation,
  useUpdateInitialChargeMutation,
} from "src/generated/graphql";
import { memo, useEffect } from "react";
import { useDisclosure } from "@mantine/hooks";
import InitialChargeFormFields, { employeeFormSchema } from "../formFields";
import FormCard from "src/components/FormCard";
import { Group, Button, useMantineTheme } from "@mantine/core";
import { useModals } from "@mantine/modals";
import { GetAgreement } from "src/api/graphql/queries/Agreement";
import { GetPackage } from "src/api/graphql/queries/Package";

interface IInputChargeFormInput
  extends Omit<
    CreateInitialChargeInput,
    "fee" | "revenueCategory" | "taxRates"
  > {
  fee?: string | undefined;
  revenueCategory?: string | undefined;
  id?: string | undefined;
  taxRates: string[];
}
type IFormData = NonNullable<
  GetAgreementQuery["agreementOne"]
>["initialCharges"][0];

interface InitialChargeDetailProps {
  formData: IFormData;
  handleModal?: (initialCharge: IFormData) => void;
  isModal?: boolean;
  pkgId?: string | undefined;
  agreementId?: string | undefined;
  disabled?: boolean;
  isAllowWrite: boolean;
}

const InitialChargeDetail = ({
  formData,
  handleModal,
  isModal,
  pkgId,
  agreementId,
  disabled,
  isAllowWrite,
}: InitialChargeDetailProps) => {
  const modals = useModals();
  const theme = useMantineTheme();
  const [isFormActive] = useDisclosure(true);
  const refetchQuery = [
    {
      query: pkgId ? GetPackage : GetAgreement,
      variables: { id: pkgId ? pkgId : agreementId },
    },
  ];
  useEffect(() => {
    formData && isModal && resetFormState();
  }, [formData, isModal]);

  const [createInitialCharge, { loading: createLoading }] =
    useCreateInitialChargeMutation({
      onCompleted: () => {
        modals.closeAll();
      },
      refetchQueries: refetchQuery,
    });

  const [updateInitialCharge, { loading: updateLoading }] =
    useUpdateInitialChargeMutation({
      onCompleted: () => {
        modals.closeAll();
      },
      refetchQueries: refetchQuery,
    });
  const [deleteInitialCharge, { loading: deleteLoading }] =
    useDeleteInitialChargeMutation({
      refetchQueries: refetchQuery,
    });

  const form = useForm<IInputChargeFormInput>({
    initialValues: {
      cost: formData?.cost || 0,
      isTaxable: formData?.isTaxable || false,
      isPostTax: formData?.isPostTax || false,
      taxRates: formData?.taxRates?.map((e) => e?.id) || [],
    },
    validate: yupResolver(employeeFormSchema),
  });

  const handleSubmit = async (values: Required<IInputChargeFormInput>) => {
    const {
      revenueCategory,
      fee,
      cost,
      isDiscountable,
      taxRates,
      isPostTax,
      isTaxable,
    } = values;
    let _obj = {
      ...(formData?.id
        ? {}
        : pkgId
        ? { package: { id: pkgId } }
        : { agreement: { id: agreementId! } }),
      revenueCategory: {
        id: revenueCategory!,
      },
      fee: fee
        ? {
            id: fee,
          }
        : undefined,
      cost: cost as number,
      isDiscountable,
      taxRates: taxRates.map((e) => ({
        id: e,
      })),
      isPostTax,
      isTaxable,
    };

    formData?.id
      ? updateInitialCharge({
          variables: {
            data: _obj,
            updateInitialChargeId: formData?.id,
          },
        })
      : createInitialCharge({
          variables: {
            data: _obj,
          },
        });
  };

  const handleDelete = () => {
    deleteInitialCharge({
      variables: { deleteInitialChargeId: formData?.id! },
    });
  };
  const handleEdit = () => {
    handleModal && handleModal(formData);
  };

  const resetFormState = () => {
    const { id, fee, revenue, taxRates, ...rest } = formData;
    let _obj = {
      ...rest,
      fee: fee?.id,
      revenueCategory: revenue?.id,
      taxRates: taxRates.map((e) => e.id),
    };
    form.setValues(_obj);
    form.resetDirty(_obj);
  };

  const title = `$${formData?.cost} \u00A0\u00A0\u00A0 ${formData?.revenue?.name}`;

  return (
    <>
      <FormCard
        title={title}
        loading={updateLoading || deleteLoading}
        onDelete={isAllowWrite ? handleDelete : undefined}
        onEdit={isAllowWrite ? handleEdit : undefined}
        onSave={form.onSubmit((values) =>
          handleSubmit(values as Required<IInputChargeFormInput>)
        )}
        isFormActive={isFormActive}
        hideCard={isModal}
        mb={10}
        size="md"
        radius={4}
        disabled={disabled}
      >
        {isModal && (
          <>
            <InitialChargeFormFields<IInputChargeFormInput>
              form={form}
              isActive={false}
            />

            <Group position="apart" mt="md" style={{ marginTop: 30 }}>
              <Button
                radius="md"
                variant="outline"
                onClick={() => modals.closeAll()}
                style={{
                  flex: 1,
                  color: theme.colors.secondaryTextColor[6],
                }}
                sx={{
                  backgroundColor: theme.colors.secondary[6],
                  "&:hover": {
                    background: theme.colors.secondary[5],
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                radius="md"
                type="submit"
                disabled={createLoading || updateLoading}
                style={{
                  flex: 1,
                }}
                sx={{
                  color: theme.colors.primaryTextColor[6],
                  "&:hover": {
                    backgroundColor: theme.colors.primary[4],
                  },
                }}
                loading={createLoading || updateLoading}
              >
                {formData?.id ? "Save" : "Create"}
              </Button>
            </Group>
          </>
        )}
      </FormCard>
    </>
  );
};

export default memo(InitialChargeDetail);
